import React, { useEffect, useImperativeHandle, useRef, forwardRef } from "react";

/**
 * Cloudflare Turnstile widget.
 *
 * Renders only when REACT_APP_TURNSTILE_SITE_KEY is configured. Without the
 * site key the component is a no-op and the parent submits without a token —
 * the backend's /services/captcha.py also fails open in that mode, keeping
 * local dev frictionless. The launch-readiness probe surfaces the missing
 * key so prod can't ship without it.
 *
 * Preview-host bypass: when running on a `*.preview.emergentagent.com`
 * hostname (Emergent's ephemeral preview environments) the widget is
 * suppressed because Cloudflare can't validate those throwaway hostnames
 * without per-fork whitelisting. The backend mirrors this bypass so the
 * auth POST still goes through. Hard-coded (not env-driven) so a
 * misconfigured env can never disable CAPTCHA on the real prod domain.
 *
 * Props:
 *   onVerify(token | null)  — called with the token on success / null on expire / error.
 *   theme  — "light" | "dark" | "auto" (defaults to "auto")
 *   testId — data-testid for the container
 */
const PREVIEW_HOST_SUFFIX = ".preview.emergentagent.com";

export function isPreviewHost() {
  if (typeof window === "undefined") return false;
  const host = (window.location?.hostname || "").toLowerCase();
  return host.endsWith(PREVIEW_HOST_SUFFIX);
}

export default forwardRef(function TurnstileWidget(
  { onVerify, theme = "auto", testId = "turnstile-widget" },
  ref,
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const callbackRef = useRef(onVerify);
  callbackRef.current = onVerify;

  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const previewHost = isPreviewHost();

  // Expose `.reset()` so callers (e.g. Login.jsx after a failed
  // attempt) can pull a fresh challenge without forcing the user to
  // refresh the whole page. Tokens are single-use, so a wrong-password
  // submit consumes the token; without a reset the submit button stays
  // disabled until the widget naturally times out or the user reloads.
  useImperativeHandle(ref, () => ({
    reset: () => {
      try {
        if (window.turnstile && widgetIdRef.current !== null) {
          window.turnstile.reset(widgetIdRef.current);
        }
      } catch { /* ignore */ }
      // Clear stale token in the parent immediately; Turnstile will
      // re-fire `callback` once the fresh challenge resolves.
      callbackRef.current && callbackRef.current(null);
    },
  }), []);

  useEffect(() => {
    if (!siteKey || previewHost) return;

    // Inject the Turnstile script once per page.
    const SCRIPT_ID = "cf-turnstile-script";
    let script = document.getElementById(SCRIPT_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (!window.turnstile || !containerRef.current) {
        setTimeout(tryRender, 150);
        return;
      }
      // Defensive — already rendered.
      if (widgetIdRef.current !== null) return;
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          callback: (token) => callbackRef.current && callbackRef.current(token),
          "error-callback": () => callbackRef.current && callbackRef.current(null),
          "expired-callback": () => callbackRef.current && callbackRef.current(null),
          "timeout-callback": () => callbackRef.current && callbackRef.current(null),
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[turnstile] render failed", err);
      }
    };
    tryRender();

    return () => {
      cancelled = true;
      try {
        if (window.turnstile && widgetIdRef.current !== null) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch { /* ignore */ }
      widgetIdRef.current = null;
    };
  }, [siteKey, theme]);

  // No site key → render nothing (auth flow proceeds without a token; backend skips verify).
  if (!siteKey) return null;
  // Preview host (Emergent ephemeral) → skip the widget entirely; backend
  // applies the same host-based bypass so the form still posts cleanly.
  if (previewHost) {
    return (
      <div
        data-testid={`${testId}-preview-bypass`}
        className="my-3 rounded-lg border border-dashed border-[#D0D0D0] bg-[#FAFAFA] px-3 py-2 text-xs text-[#6B6B6B]"
      >
        Bot protection is bypassed in preview environments. Production will require the Cloudflare challenge.
      </div>
    );
  }

  return <div ref={containerRef} data-testid={testId} className="my-3" />;
});
